import { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Banknote, Smartphone, CreditCard, CheckCircle2, XCircle, Receipt, Zap, Crown, User, Plus, Users, Fingerprint, Search, AlertTriangle } from "lucide-react";
import { INR, api } from "../lib/api";
import { computeLiveBreakdown } from "../lib/billing";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "./ui/tabs";

const PAY_MODES = [
  { id: "cash", label: "Cash", icon: Banknote },
  { id: "upi", label: "UPI", icon: Smartphone },
  { id: "card", label: "Card", icon: CreditCard },
];

function pad(n) {
  return String(n).padStart(2, "0");
}

function fmt(d) {
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export default function StopSessionModal({
  open,
  tableSession,
  settings,
  paymentCfg,
  players,
  nowTick,
  onClose,
  onConfirm,
  onPayRazorpay,
}) {
  const [mode, setMode] = useState("cash");
  const [status, setStatus] = useState("paid");
  // Player capture
  const [playerMode, setPlayerMode] = useState("new"); // new | existing
  const [name, setName] = useState("");
  const [savePlayer, setSavePlayer] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");

  // Cross-club Portal Player linkage — REQUIRED before billing.
  // If the session was started with a portal player, we pre-select & lock it.
  const sessionPortalId = tableSession?.current_session?.portal_player_id || "";
  const [portalPlayerId, setPortalPlayerId] = useState("");
  const [portalPlayers, setPortalPlayers] = useState([]);
  const [portalSearch, setPortalSearch] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);

  const sessPlayerType = tableSession?.current_session?.player_type;
  // Admin can correct a mis-picked Club/Cross-Club at Stop time. The rate
  // bucket used at billing follows this effective type. Reset each time
  // the modal is (re)opened for a different session.
  const [effectiveType, setEffectiveType] = useState(sessPlayerType || "non_member");
  const playersOfType = useMemo(
    () => (players || []).filter((p) => p.type === effectiveType),
    [players, effectiveType]
  );
  const filteredPlayers = useMemo(
    () =>
      playersOfType.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase())
      ),
    [playersOfType, search]
  );

  useEffect(() => {
    if (open) {
      setMode("cash");
      setStatus("paid");
      // Reset the effective player_type to whatever the session was started with.
      setEffectiveType(sessPlayerType || "non_member");
      // Pre-fill the name input with whatever the session already has, unless
      // it's the placeholder "Player" (sessions started without a name).
      const initialName =
        tableSession?.current_session?.player_name &&
        tableSession.current_session.player_name !== "Player"
          ? tableSession.current_session.player_name
          : "";
      setName(initialName);
      setSavePlayer(false);
      setSelectedId("");
      setSearch("");
      setPlayerMode(playersOfType.length > 0 ? "existing" : "new");
      // Reset / preload portal-player selection.
      setPortalPlayerId(sessionPortalId);
      setPortalSearch("");
      // Fetch the full registered-players list (cafe-scoped on backend).
      setPortalLoading(true);
      api
        .get("/portal/players", { params: { limit: 500 } })
        .then(({ data }) => setPortalPlayers(Array.isArray(data) ? data : []))
        .catch(() => setPortalPlayers([]))
        .finally(() => setPortalLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lookup helper — display label of selected portal player.
  const portalSelected = useMemo(
    () => portalPlayers.find((p) => p.id === portalPlayerId) || null,
    [portalPlayers, portalPlayerId],
  );
  const filteredPortal = useMemo(() => {
    const q = portalSearch.trim().toLowerCase();
    if (!q) return portalPlayers.slice(0, 25);
    return portalPlayers
      .filter(
        (p) =>
          (p.full_name || "").toLowerCase().includes(q) ||
          (p.phone || "").includes(q) ||
          (p.unique_code || "").toLowerCase().includes(q),
      )
      .slice(0, 25);
  }, [portalPlayers, portalSearch]);

  // Was the session started already linked to a portal player? Lock the field
  // so the admin can't accidentally re-attribute a billed session.
  const portalLocked = !!sessionPortalId;

  const finalized = tableSession?.finalized;
  const finalSession = tableSession?.finalSession;
  const sess = finalSession || tableSession?.current_session;

  const live = useMemo(() => {
    if (!sess || finalized) return null;
    const rateNormal =
      effectiveType === "member"
        ? sess.rate_member
        : effectiveType === "cross_club_member"
        ? (sess.rate_cross_club_member ?? sess.rate_member)
        : sess.rate_non_member;
    return computeLiveBreakdown(sess, nowTick, settings, rateNormal);
  }, [sess, nowTick, settings, finalized, effectiveType]);

  if (!tableSession) return null;
  const breakdown = finalized ? finalSession.breakdown : live;
  const totalMin = breakdown?.total_minutes || 0;
  const h = pad(Math.floor(totalMin / 60));
  const m = pad(totalMin % 60);
  const amount = finalized ? finalSession.amount : breakdown?.amount || 0;

  // Build the player part of the stop payload from the current state.
  const buildPlayerPayload = () => {
    const base = {
      portal_player_id: portalPlayerId || null,
      // Only send override when it differs from the original session type,
      // so we don't create noise for correctly-picked sessions.
      override_player_type:
        effectiveType && effectiveType !== sessPlayerType ? effectiveType : null,
    };
    if (playerMode === "existing" && selectedId) {
      return { ...base, player_id: selectedId, player_name: null, save_player: false };
    }
    const trimmed = (name || "").trim();
    if (!trimmed) {
      // Allow stopping with no name — backend keeps the existing one
      // (or the "Player" placeholder) so this never blocks a stop.
      return { ...base, player_id: null, player_name: null, save_player: false };
    }
    return { ...base, player_id: null, player_name: trimmed, save_player: savePlayer };
  };

  // Friendlier display name on the receipt header while editing
  const displayName =
    playerMode === "existing"
      ? (players?.find((p) => p.id === selectedId)?.name ||
         sess?.player_name ||
         "—")
      : (name.trim() || sess?.player_name || "—");

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="stop-session-modal"
        className="bg-[#121a16] border border-white/10 text-white max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <Receipt className="w-5 h-5 text-amber-400" />
            {finalized ? "Receipt" : "End Session"}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {sess?.table_name} · {sess?.player_name}
          </DialogDescription>
        </DialogHeader>

        {/* Receipt-style breakdown — compact so it never pushes the End button off-screen */}
        <div
          data-testid="receipt-block"
          className="bg-[#0a0f0d] p-3.5 rounded-xl border border-white/5 font-mono text-[13px] text-gray-300 relative overflow-hidden"
        >
          <div className="absolute -top-3 left-0 right-0 h-3 bg-[repeating-linear-gradient(90deg,transparent_0_8px,#0a0f0d_8px_16px)]" />
          <div className="flex justify-between text-xs text-gray-500 mb-3">
            <span>CUE GENIC</span>
            <span>{fmt(sess?.start_time)}</span>
          </div>
          <div className="space-y-1.5">
            <Row k="Table" v={sess?.table_name} />
            <Row k="Player" v={finalized ? sess?.player_name : displayName} />
            <Row
              k="Type"
              v={
                effectiveType === "member"
                  ? "Club Member"
                  : effectiveType === "cross_club_member"
                  ? "Cross-Club Member"
                  : "Non-Member"
              }
            />
            <Row k="Start" v={fmt(sess?.start_time)} />
            <Row
              k="End"
              v={finalized ? fmt(finalSession.end_time) : fmt(nowTick)}
            />
            <div className="border-t border-dashed border-white/10 my-3" />
            <Row k="Duration" v={`${h}h ${m}m  (${totalMin} min)`} />
            {breakdown?.happy_minutes > 0 && (
              <Row
                k={`Happy Hour ${breakdown.happy_minutes}m @ ₹${breakdown.rate_happy}/hr`}
                v={INR(breakdown.amount_happy)}
              />
            )}
            {breakdown?.normal_minutes > 0 && (
              <Row
                k={`${
                  effectiveType === "member"
                    ? "Club Member"
                    : effectiveType === "cross_club_member"
                    ? "Cross-Club Member"
                    : "Non-Member"
                } ${breakdown.normal_minutes}m @ ₹${breakdown.rate_normal}/hr`}
                v={INR(breakdown.amount_normal)}
              />
            )}
            <div className="border-t border-dashed border-white/10 my-3" />
            <div className="flex justify-between items-center">
              <span className="text-amber-300 font-bold uppercase tracking-wider">
                Total
              </span>
              <span
                data-testid="receipt-total"
                className="text-amber-300 text-2xl font-bold tabular-nums"
              >
                {INR(amount)}
              </span>
            </div>
          </div>
        </div>

        {!finalized && (
          <div className="space-y-4 mt-2">
            {/* ---- Player-type switch (correct a mis-picked type at Stop) ---- */}
            <div data-testid="stop-type-switch" className="rounded-xl bg-black/30 border border-white/10 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold">
                  Player type
                </div>
                {effectiveType !== sessPlayerType && (
                  <span className="text-[9px] uppercase tracking-widest font-bold text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded px-1.5 py-0.5" data-testid="stop-type-switched-badge">
                    Switched
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-1.5 p-1 rounded-lg bg-black/40 border border-white/5">
                <TypeChip
                  active={effectiveType === "member"}
                  onClick={() => setEffectiveType("member")}
                  testId="stop-type-club-member"
                  label="Club"
                  activeCls="bg-amber-500 text-black"
                />
                <TypeChip
                  active={effectiveType === "cross_club_member"}
                  onClick={() => setEffectiveType("cross_club_member")}
                  testId="stop-type-cross-club"
                  label="Cross-Club"
                  activeCls="bg-sky-500 text-black"
                />
                <TypeChip
                  active={effectiveType === "non_member"}
                  onClick={() => setEffectiveType("non_member")}
                  testId="stop-type-non-member"
                  label="Non-Member"
                  activeCls="bg-emerald-500 text-black"
                />
              </div>
              <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                Wrong type at start? Tap the correct one — the rate re-calculates instantly.
              </p>
            </div>

            {/* ---- Registered Portal Player (only for Cross-Club Member) ---- */}
            {effectiveType === "cross_club_member" && (
            <div data-testid="stop-portal-section">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-[0.2em] text-gray-400 flex items-center gap-2">
                  <Fingerprint className="w-3.5 h-3.5 text-emerald-300" />
                  Registered Player <span className="text-rose-400">*</span>
                </div>
                {portalLocked && (
                  <span className="text-[10px] uppercase tracking-widest text-emerald-300/80">
                    locked — set at start
                  </span>
                )}
              </div>

              {portalSelected ? (
                <div
                  data-testid="stop-portal-selected"
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-emerald-400/40 bg-emerald-400/10"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {portalSelected.photo_b64 ? (
                      <img src={portalSelected.photo_b64} alt="" className="h-9 w-9 rounded-md object-cover" />
                    ) : (
                      <div className="h-9 w-9 rounded-md bg-emerald-900/60 grid place-items-center text-emerald-200 text-sm">
                        {portalSelected.full_name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-emerald-100 truncate">
                        {portalSelected.full_name}
                        {portalSelected.is_blacklisted && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-400/40">
                            BLACKLISTED
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-emerald-200/70 truncate">
                        {portalSelected.unique_code} · {portalSelected.phone}
                      </div>
                    </div>
                  </div>
                  {!portalLocked && (
                    <button
                      type="button"
                      onClick={() => setPortalPlayerId("")}
                      data-testid="stop-portal-change"
                      className="text-[11px] uppercase tracking-widest text-emerald-200 hover:text-white"
                    >
                      Change
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                    <Input
                      data-testid="stop-portal-search"
                      value={portalSearch}
                      onChange={(e) => setPortalSearch(e.target.value)}
                      placeholder="Search by name, phone, or CGP-XXXX-XXXX…"
                      className="pl-9 bg-black/40 border-white/10 text-white"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                    {portalLoading && (
                      <div className="text-xs text-gray-500 text-center py-3">Loading…</div>
                    )}
                    {!portalLoading && filteredPortal.length === 0 && (
                      <div className="text-xs text-gray-500 text-center py-3" data-testid="stop-portal-empty">
                        {portalPlayers.length === 0
                          ? "No registered players yet — open the Players Portal to add one."
                          : "No matches. Try the player's CGP code or phone."}
                      </div>
                    )}
                    {filteredPortal.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`stop-portal-pick-${p.id}`}
                        onClick={() => setPortalPlayerId(p.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg border bg-black/30 transition flex items-center justify-between ${
                          p.is_blacklisted
                            ? "border-rose-500/40 hover:border-rose-400/60"
                            : "border-white/10 hover:border-emerald-400/40"
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-white truncate flex items-center gap-2">
                            {p.full_name}
                            {p.is_blacklisted && (
                              <AlertTriangle className="w-3 h-3 text-rose-400 shrink-0" />
                            )}
                          </div>
                          <div className="text-[10px] font-mono text-gray-400 truncate">
                            {p.unique_code} · {p.phone}
                          </div>
                        </div>
                        <span className="text-[10px] uppercase tracking-widest text-gray-500">
                          {p.history_count || 0}×
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="text-[11px] text-amber-300/80 bg-amber-500/10 border border-amber-400/20 rounded-md px-3 py-2 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div>
                      A registered player must be selected before the bill can be generated.
                      Walk-in? <span className="text-amber-200 font-bold">Open the Players Portal → Add Player</span> and come back.
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* ---- Player capture ---- */}
            <div data-testid="stop-player-section">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Player
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${
                    effectiveType === "member"
                      ? "bg-amber-400/15 text-amber-200 border-amber-400/40"
                      : effectiveType === "cross_club_member"
                      ? "bg-sky-400/15 text-sky-200 border-sky-400/40"
                      : "bg-emerald-400/15 text-emerald-200 border-emerald-400/40"
                  }`}
                >
                  {effectiveType === "member" || effectiveType === "cross_club_member" ? (
                    <Crown className="w-3 h-3" />
                  ) : (
                    <User className="w-3 h-3" />
                  )}
                  {effectiveType === "member"
                    ? "Club Member"
                    : effectiveType === "cross_club_member"
                    ? "Cross-Club Member"
                    : "Non-Member"}
                </span>
              </div>

              <Tabs value={playerMode} onValueChange={setPlayerMode}>
                <TabsList className="bg-black/40 border border-white/10 grid grid-cols-2 w-full">
                  <TabsTrigger
                    value="existing"
                    data-testid="stop-tab-existing"
                    disabled={playersOfType.length === 0}
                    className="data-[state=active]:bg-amber-500 data-[state=active]:text-black font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Users className="w-3.5 h-3.5 mr-1.5" />
                    Saved
                    <span className="ml-1.5 text-[10px] opacity-70">({playersOfType.length})</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="new"
                    data-testid="stop-tab-new"
                    className="data-[state=active]:bg-amber-500 data-[state=active]:text-black font-bold"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    New
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="existing" className="mt-3 space-y-2">
                  <Input
                    data-testid="stop-player-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Search ${sessPlayerType === "member" ? "members" : "non-members"}…`}
                    className="bg-black/40 border-white/10 text-white"
                  />
                  <div className="max-h-44 overflow-y-auto space-y-1.5 pr-1">
                    {filteredPlayers.length === 0 && (
                      <div className="text-xs text-gray-500 text-center py-3">
                        {playersOfType.length === 0
                          ? `No saved ${sessPlayerType === "member" ? "members" : "non-members"} yet — switch to "New".`
                          : "No matches."}
                      </div>
                    )}
                    {filteredPlayers.map((p) => (
                      <button
                        key={p.id}
                        data-testid={`stop-pick-${p.id}`}
                        onClick={() => setSelectedId(p.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg border flex items-center justify-between transition ${
                          selectedId === p.id
                            ? "border-amber-400 bg-amber-400/10"
                            : "border-white/10 hover:border-white/20 bg-black/30"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {p.type === "member" ? (
                            <Crown className="w-3.5 h-3.5 text-amber-400" />
                          ) : (
                            <User className="w-3.5 h-3.5 text-gray-400" />
                          )}
                          <span className="font-bold text-sm">{p.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="new" className="mt-3 space-y-3">
                  <Input
                    data-testid="stop-player-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Rohan Sharma (optional)"
                    className="bg-black/40 border-white/10 text-white"
                  />
                  <div className="flex items-center justify-between bg-black/40 border border-white/10 rounded-lg px-3 py-2">
                    <div>
                      <div className="text-sm font-bold">Save to players list</div>
                      <div className="text-[11px] text-gray-500">
                        Reuse this {sessPlayerType === "member" ? "member" : "non-member"} later
                      </div>
                    </div>
                    <Switch
                      data-testid="stop-save-player"
                      checked={savePlayer}
                      onCheckedChange={setSavePlayer}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-2">
                Payment Mode
              </div>
              <div className="grid grid-cols-3 gap-2">
                {PAY_MODES.map(({ id, label, icon: Icon }) => {
                  const usesGateway =
                    paymentCfg?.razorpay_enabled && (id === "upi" || id === "card");
                  return (
                    <button
                      key={id}
                      data-testid={`pay-mode-${id}`}
                      onClick={() => setMode(id)}
                      className={`relative px-2 py-3 rounded-lg border-2 flex flex-col items-center gap-1 text-xs font-bold uppercase tracking-wider transition ${
                        mode === id
                          ? "border-amber-400 bg-amber-400/10 text-amber-200"
                          : "border-white/10 text-gray-400 hover:border-white/20"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                      {usesGateway && (
                        <span className="absolute -top-1.5 -right-1.5 bg-blue-500/90 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full tracking-widest shadow flex items-center gap-0.5">
                          <Zap className="w-2 h-2" />
                          LIVE
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {mode === "cash" && (
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-2">
                  Payment Status
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="status-paid"
                    onClick={() => setStatus("paid")}
                    className={`px-3 py-3 rounded-lg border-2 flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-sm transition ${
                      status === "paid"
                        ? "border-emerald-400 bg-emerald-400/15 text-emerald-200"
                        : "border-white/10 text-gray-400 hover:border-white/20"
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" /> Paid
                  </button>
                  <button
                    data-testid="status-unpaid"
                    onClick={() => setStatus("unpaid")}
                    className={`px-3 py-3 rounded-lg border-2 flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-sm transition ${
                      status === "unpaid"
                        ? "border-red-400 bg-red-400/15 text-red-200"
                        : "border-white/10 text-gray-400 hover:border-white/20"
                    }`}
                  >
                    <XCircle className="w-4 h-4" /> Unpaid
                  </button>
                </div>
              </div>
            )}

            {mode !== "cash" && paymentCfg?.razorpay_enabled && (
              <div className="text-[11px] text-blue-200/70 bg-blue-500/10 border border-blue-400/20 rounded-md px-3 py-2 leading-relaxed">
                <Zap className="w-3 h-3 inline mr-1 -mt-0.5 text-amber-300" />
                Tapping <span className="font-bold uppercase">End &amp; Pay</span> opens Razorpay Checkout. The session is marked Paid only after Razorpay confirms.
              </div>
            )}

            {mode !== "cash" && !paymentCfg?.razorpay_enabled && (
              <div className="text-[11px] text-amber-200/80 bg-amber-500/10 border border-amber-400/20 rounded-md px-3 py-2 leading-relaxed">
                Razorpay isn&apos;t configured — this will be recorded as an offline {mode.toUpperCase()} payment.
              </div>
            )}
          </div>
        )}

        {finalized && (
          <div className="text-center text-sm text-gray-400 mt-3">
            Paid via{" "}
            <span className="text-white font-bold uppercase">
              {finalSession.payment_mode}
            </span>{" "}
            ·{" "}
            <span
              className={`uppercase font-bold ${
                finalSession.payment_status === "paid"
                  ? "text-emerald-400"
                  : "text-red-400"
              }`}
            >
              {finalSession.payment_status}
            </span>
          </div>
        )}

        <DialogFooter
          className="mt-3 gap-2 sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-[#121a16] border-t border-white/10"
        >
          {!finalized ? (
            <>
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="cancel-stop-btn"
                className="text-gray-400 hover:bg-white/5"
              >
                Cancel
              </Button>
              {mode !== "cash" && paymentCfg?.razorpay_enabled ? (
                <Button
                  onClick={() => onPayRazorpay(mode, buildPlayerPayload())}
                  data-testid="end-and-pay-btn"
                  disabled={effectiveType === "cross_club_member" && !portalPlayerId}
                  className="bg-gradient-to-r from-[#1f4d8a] to-[#2563a6] hover:from-[#2563a6] hover:to-[#1f4d8a] text-white font-black uppercase tracking-widest border border-blue-400/30 shadow-lg shadow-blue-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Zap className="w-4 h-4 mr-1.5 text-amber-300" />
                  End &amp; Pay {INR(amount)}
                </Button>
              ) : (
                <Button
                  onClick={() =>
                    onConfirm({
                      payment_mode: mode,
                      payment_status: status,
                      ...buildPlayerPayload(),
                      _optimistic_breakdown: live,
                    })
                  }
                  data-testid="confirm-stop-btn"
                  disabled={effectiveType === "cross_club_member" && !portalPlayerId}
                  title={
                    effectiveType === "cross_club_member" && !portalPlayerId
                      ? "Select a registered Cross-Club player first"
                      : ""
                  }
                  className="bg-red-500 hover:bg-red-600 text-white font-black uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  End &amp; Bill
                </Button>
              )}
            </>
          ) : (
            <Button
              onClick={onClose}
              data-testid="close-receipt-btn"
              className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-gray-500">{k}</span>
      <span className="text-white tabular-nums">{v}</span>
    </div>
  );
}

function TypeChip({ active, onClick, testId, label, activeCls }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={
        "px-2.5 py-1.5 rounded-md text-[11px] font-black uppercase tracking-widest transition-colors " +
        (active ? activeCls : "text-gray-400 hover:text-white bg-black/30")
      }
    >
      {label}
    </button>
  );
}
